import css from './Wallet.module.scss'
import React, { useEffect, useRef, useState } from 'react'

import { MAX_BULK_REQUEST_SIZE } from 'meta/pages/v4/bulk'
import { IWalletEntityType, IWalletScreen } from 'meta/pages/v4/wallet'
import { useAppDispatch } from 'store'
import { WalletActions } from 'store/pages/wallet'
import { chunkArrayInGroups } from 'utils/array'

import withTranslation from 'components/HOC/WithTranslation'
import { Grid, GridContainer } from 'components/Layout/Grid'
import { useIsAppInitialized } from 'hooks/useApp'
import { useAuthToken } from 'hooks/useAuth'
import { useOnUpdate } from 'hooks/useOnUpdate'
import {
  useWalletPastEvents,
  useWalletPastEventsMetadata,
  useWalletPastGoodsServices,
  useWalletPastGoodsServicesMetadata,
  useWalletUpcomingEvents,
  useWalletUpcomingEventsMetadata,
  useWalletUpcomingGoodsServices,
  useWalletUpcomingGoodsServicesMetadata,
} from 'hooks/useWallet'

import EventCollectionDataFetcher from './BulkDataFetcher/TicketDataFetcher/EventCollectionDataFetcher'
import EventDataFetcher from './BulkDataFetcher/TicketDataFetcher/EventDataFetcher'
import WalletTicketsSkeleton from './WalletTickets/WalletTicketsSkeleton'
import Header from './Header'
import NoElements from './NoElements'
import WalletExtra from './WalletExtra'
import WalletTickets from './WalletTickets'

const Wallet: React.FC = () => {
  const appInitialized = useIsAppInitialized()
  const authToken = useAuthToken()

  const pastEvents = useWalletPastEvents()
  const upcomingEvents = useWalletUpcomingEvents()

  const pastGoodsServices = useWalletPastGoodsServices()
  const upcomingGoodsServices = useWalletUpcomingGoodsServices()

  const pastEventsMetadata = useWalletPastEventsMetadata()
  const upcomingEventsMetadata = useWalletUpcomingEventsMetadata()

  const pastGoodsServicesMetadata = useWalletPastGoodsServicesMetadata()
  const upcomingGoodsServicesMetadata = useWalletUpcomingGoodsServicesMetadata()

  const [eventActive, setEventActive] = useState<number | null>(null)
  const [useLongCard, setUseLongCard] = useState<boolean>(false)

  const [arePastEventsVisible, setPastEventsVisible] = useState<boolean>(false)
  const [arePastGoodsServicesVisible, setPastGoodsServicesVisible] = useState<boolean>(false)

  const [activeScreen, setActiveScreen] = useState<IWalletScreen | null>(IWalletScreen.tickets)

  const lastScrollY = useRef<number>(0)

  const dispatch = useAppDispatch()

  const handleActiveEventChange = (eventId: number) => {
    setEventActive((prevState) => (prevState === null ? eventId : null))
  }

  useEffect(() => {
    if (appInitialized && authToken) {
      setEventActive(null)
      dispatch(WalletActions.getPastEvents({ page: 1 }))
      dispatch(WalletActions.getUpcomingEvents({ page: 1 }))
    }
  }, [appInitialized, authToken])

  useEffect(() => {
    setEventActive(null)

    if (
      activeScreen == IWalletScreen.good_service &&
      Object.values(upcomingGoodsServices).flat().length === 0 &&
      Object.values(pastGoodsServices).flat().length === 0
    ) {
      dispatch(WalletActions.getPastGoodsServices({ page: 1 }))
      dispatch(WalletActions.getUpcomingGoodsServices({ page: 1 }))
    }
  }, [activeScreen])

  useEffect(() => {
    setEventActive(null)
  }, [activeScreen])

  useOnUpdate(() => {
    if (eventActive) {
      lastScrollY.current = window.scrollY
      window.scrollTo({ behavior: 'smooth', top: 0 })
    } else {
      window.scrollTo({ behavior: 'smooth', top: lastScrollY.current })
    }
  }, [eventActive])

  useEffect(() => {
    if (appInitialized && authToken) {
      if (pastEventsMetadata.hasMorePages && arePastEventsVisible) {
        dispatch(WalletActions.getPastEvents({ page: pastEventsMetadata.currentPage + 1 }))
      }
    }
  }, [pastEventsMetadata, arePastEventsVisible])

  useEffect(() => {
    if (appInitialized && authToken) {
      if (upcomingEventsMetadata.hasMorePages) {
        dispatch(WalletActions.getUpcomingEvents({ page: upcomingEventsMetadata.currentPage + 1 }))
      }
    }
  }, [upcomingEventsMetadata])

  useEffect(() => {
    if (appInitialized && authToken) {
      if (
        pastGoodsServicesMetadata.hasMorePages &&
        arePastGoodsServicesVisible &&
        activeScreen == IWalletScreen.good_service
      ) {
        dispatch(WalletActions.getPastGoodsServices({ page: pastGoodsServicesMetadata.currentPage + 1 }))
      }
    }
  }, [pastGoodsServicesMetadata, arePastGoodsServicesVisible, activeScreen])

  useEffect(() => {
    if (appInitialized && authToken && activeScreen == IWalletScreen.good_service) {
      if (upcomingGoodsServicesMetadata.hasMorePages) {
        dispatch(WalletActions.getUpcomingGoodsServices({ page: upcomingGoodsServicesMetadata.currentPage + 1 }))
      }
    }
  }, [upcomingGoodsServicesMetadata, activeScreen])

  return (
    <div className={css.wallet}>
      <GridContainer className={css.header}>
        <Grid>
          {!eventActive && <Header setActiveScreen={setActiveScreen} activeScreen={activeScreen} />}

          {activeScreen == IWalletScreen.tickets &&
            Object.values(upcomingEvents).flat().length === 0 &&
            Object.values(pastEvents).flat().length === 0 &&
            upcomingEventsMetadata.currentPage == 1 &&
            upcomingEventsMetadata.hasMorePages == false &&
            pastEventsMetadata.currentPage == 1 &&
            pastEventsMetadata.hasMorePages == false && <NoElements activeScreen={activeScreen} />}

          {activeScreen == IWalletScreen.good_service &&
            Object.values(upcomingGoodsServices).flat().length === 0 &&
            Object.values(pastGoodsServices).flat().length === 0 &&
            upcomingGoodsServicesMetadata.currentPage == 1 &&
            upcomingGoodsServicesMetadata.hasMorePages == false &&
            pastGoodsServicesMetadata.currentPage == 1 &&
            pastGoodsServicesMetadata.hasMorePages == false && <NoElements activeScreen={activeScreen} />}
        </Grid>
      </GridContainer>

      {activeScreen == IWalletScreen.tickets &&
        (Object.values(upcomingEvents).flat().length > 0 || Object.values(pastEvents).flat().length > 0 ? (
          <WalletTickets
            upcomingEvents={Object.values(upcomingEvents).flat()}
            pastEvents={Object.values(pastEvents).flat()}
            useLongCard={useLongCard}
            eventActive={eventActive}
            setEventActive={handleActiveEventChange}
            setUseLongCard={setUseLongCard}
            setPastEventsVisible={setPastEventsVisible}
          />
        ) : (
          upcomingEventsMetadata.hasMorePages && pastEventsMetadata.hasMorePages && <WalletTicketsSkeleton />
        ))}

      {activeScreen == IWalletScreen.good_service &&
        (Object.values(upcomingGoodsServices).flat().length > 0 ||
        Object.values(pastGoodsServices).flat().length > 0 ? (
          <WalletExtra
            upcomingGoodsServices={Object.values(upcomingGoodsServices).flat()}
            pastGoodsServices={Object.values(pastGoodsServices).flat()}
            useLongCard={useLongCard}
            eventActive={eventActive}
            setEventActive={handleActiveEventChange}
            setUseLongCard={setUseLongCard}
            setPastGoodsServicesVisible={setPastGoodsServicesVisible}
          />
        ) : (
          upcomingGoodsServicesMetadata.hasMorePages &&
          pastGoodsServicesMetadata.hasMorePages && <WalletTicketsSkeleton />
        ))}

      {Object.values(pastEvents).length > 0 &&
        Object.values(pastEvents).map((el) =>
          chunkArrayInGroups(el, MAX_BULK_REQUEST_SIZE).map((events, key) => {
            const singleEvents = events.filter((x) => x.type == IWalletEntityType.event)
            const eventCollections = events.filter((x) => x.type == IWalletEntityType.event_collection)

            return (
              <div key={key}>
                {singleEvents.length > 0 && <EventDataFetcher eventIds={singleEvents.map((x) => x.id)} />}
                {eventCollections.length > 0 && (
                  <EventCollectionDataFetcher collectionIds={eventCollections.map((x) => x.id)} />
                )}
              </div>
            )
          })
        )}

      {Object.values(upcomingEvents).length > 0 &&
        Object.values(upcomingEvents).map((el) =>
          chunkArrayInGroups(el, MAX_BULK_REQUEST_SIZE).map((events, key) => {
            const singleEvents = events.filter((x) => x.type == IWalletEntityType.event)
            const eventCollections = events.filter((x) => x.type == IWalletEntityType.event_collection)

            return (
              <div key={key}>
                {singleEvents.length > 0 && <EventDataFetcher eventIds={singleEvents.map((x) => x.id)} />}
                {eventCollections.length > 0 && (
                  <EventCollectionDataFetcher collectionIds={eventCollections.map((x) => x.id)} />
                )}
              </div>
            )
          })
        )}

      {Object.values(pastGoodsServices).length > 0 &&
        Object.values(pastGoodsServices).map((el) =>
          chunkArrayInGroups(el, MAX_BULK_REQUEST_SIZE).map((events, key) => (
            <EventDataFetcher key={key} eventIds={events.map((x) => x.id)} />
          ))
        )}

      {Object.values(upcomingGoodsServices).length > 0 &&
        Object.values(upcomingGoodsServices).map((el) =>
          chunkArrayInGroups(el, MAX_BULK_REQUEST_SIZE).map((events, key) => (
            <EventDataFetcher key={key} eventIds={events.map((x) => x.id)} />
          ))
        )}
    </div>
  )
}

const namespaces = ['wallet', 'goodsAndServices']

export default withTranslation(namespaces)(Wallet)
